import React, { useEffect, useState } from "react";
import { contributionService, Contributor } from "../../lib/firebase";
import { Button } from "../../components/ui/button";

function getInitials(name: string) {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function getRandomAnon() {
  return `Anonymous-${Math.floor(10000 + Math.random() * 90000)}`;
}
function getProfileLink(contributor: Contributor) {
  if (contributor.linkedin) return contributor.linkedin;
  if (contributor.twitter) return contributor.twitter;
  if (contributor.reddit) return contributor.reddit;
  return null;
}
function getLinkedInPhotoUrl(linkedin: string | undefined) {
  if (!linkedin) return null;
  const match = linkedin.match(/linkedin\.com\/in\/([a-zA-Z0-9-_]+)/);
  if (match) {
    return `https://www.linkedin.com/in/${match[1]}/overlay/photo/`;
  }
  return null;
}

function ContributorAvatar({ name, avatar }: { name: string; avatar?: string | null }) {
  const initials = getInitials(name);
  return (
    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-3 overflow-hidden border-2 border-[#daed6e]" style={{background: 'linear-gradient(135deg, #daed6e 60%, #e1dcdc 100%)'}}>
      {avatar ? (
        <img src={avatar} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-3xl font-bold text-gray-800">{initials}</span>
      )}
    </div>
  );
}

const ContributorsPage = () => {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contributionService.getContributors().then((data) => {
      setContributors(data);
      setLoading(false);
    });
  }, []);

  // --- Main Nav Bar ---
  const mainNav = (
    <div className="fixed w-full px-4 top-[46px] z-10">
      <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
        <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
        <div className="relative h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.location.href = '/'}>
            <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
            <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
          </div>
          <Button className="w-[166px] h-[49px] bg-[#daed6e] rounded-[20px] border border-solid border-[#e7e7e7] hover:bg-[#c5d85c]" onClick={() => window.location.href = '/join'}>
            <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">JOIN</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#e1dcdc75] pb-16 pt-[140px] px-4">
      {mainNav}
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-4">RAWPA Contributors</h1>
        <p className="text-center text-gray-600 mb-8">
          This page is a promise to the community: everyone whose contribution is accepted is listed here. Thank you for making RAWPA better!
        </p>
        {loading ? (
          <div className="text-center text-gray-500">Loading...</div>
        ) : contributors.length === 0 ? (
          <div className="text-center text-gray-500">No contributors yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contributors.map((c) => {
              let name = c.name?.trim() || '';
              if (!name) name = getRandomAnon();
              let link = getProfileLink(c);
              return (
                <div key={c.id} className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
                  <ContributorAvatar name={name} avatar={c.avatar} />
                  {link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer" className="font-semibold text-lg text-blue-700 hover:underline mb-1">{name}</a>
                  ) : (
                    <div className="font-semibold text-lg mb-1">{name}</div>
                  )}
                  <div className="text-xs text-gray-500 mb-2">{c.contributionType}</div>
                  <div className="flex gap-2 mb-2">
                    {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-700">LinkedIn</a>}
                    {c.twitter && <a href={c.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-400">Twitter</a>}
                    {c.reddit && <a href={c.reddit} target="_blank" rel="noopener noreferrer" className="text-orange-600">Reddit</a>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributorsPage; 